import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { afterRender } from '../../../../common/after-render';
import { isEmpty, isStringEmpty, isStringEqual } from '../../../../common/functions';
import { LockInitialWidthDirective } from '../../../../directives/lock-initial-width.directive';

@Component({
  selector: 'app-container-infestation-rate',
  standalone: true,
  imports: [
    SelectModule,
    CommonModule,
    FormsModule,
    ToggleSwitchModule,
    InputNumberModule,
    FloatLabelModule,
    CardModule,
    LockInitialWidthDirective,
    TooltipModule
  ],
  templateUrl: './container-infestation-rate.component.html'
})
export class ContainerInfestationRateComponent implements OnChanges, OnDestroy {
  insectsContainersConcatenated: any[] = [];
  quantityPerContainer: number | null = null;
  selectedContainer: any;
  private quantityDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  @ViewChild('containerSelect', { read: ElementRef }) containerSelect!: ElementRef;
  @Input() catalogInformation!: any[];
  @Input() isDisabled: boolean = false;
  @Input() reset: boolean = false;
  @Input() containerItemChanges: any;
  @Input() editingOrderItemContainerInfestation: any;
  @Input() selectedCatalogInsectGrowthStageId: string | null = null;
  @Input() numberOfContainers: number | null = null;
  @Input() editingInstance: boolean = false;
  @Input() editingMode!: string;
  @Output() public containerItemChangeEvent = new EventEmitter<any>();
  standardTooltipText: string =
    'By default, the insectary team will use their standard process to fill your order. If you would like to customize your request, turn off the "Standard" and enter your custom amount below.';

  containerItem: {
    catalogContainerInsectGrowthStageId: string | null;
    standard: boolean;
    quantityPerContainer: number | null;
    numberOfContainers: number | null;
  } = {
    catalogContainerInsectGrowthStageId: null,
    standard: true,
    quantityPerContainer: null,
    numberOfContainers: null
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reset'] && changes['reset'].currentValue) {
      this.clearQuantityDebounceTimer();
      this.containerItem = { catalogContainerInsectGrowthStageId: null, standard: true, quantityPerContainer: null, numberOfContainers: null };
      afterRender(() => this.emitContainerItem());
    }

    if (changes['selectedCatalogInsectGrowthStageId'] && !isEmpty(this.selectedCatalogInsectGrowthStageId)) {
      this.insectsContainersConcatenated = this.getInsectsContainers(this.selectedCatalogInsectGrowthStageId!);
      const hasEditingSelection = !isStringEmpty(this.editingOrderItemContainerInfestation?.catalogContainerInsectGrowthStageId);
      if (!hasEditingSelection) {
        this.containerItem.catalogContainerInsectGrowthStageId = null;
      }
    }

    if (changes['editingOrderItemContainerInfestation']) {
      if (!isStringEmpty(this.editingOrderItemContainerInfestation?.catalogContainerInsectGrowthStageId)) {
        const containerSelected = this.catalogInformation
          .flatMap((info: any) => info.Containers)
          .find((con: any) =>
            isStringEqual(con.CatalogContainerInsectGrowthStageId, this.editingOrderItemContainerInfestation.catalogContainerInsectGrowthStageId)
          );

        if (containerSelected) {
          this.insectsContainersConcatenated = this.getInsectsContainers(containerSelected.CatalogInsectGrowthStageId);
          this.containerItem.catalogContainerInsectGrowthStageId = containerSelected.CatalogContainerInsectGrowthStageId;
          this.containerItem.standard = this.editingOrderItemContainerInfestation.standardInfestationRate;
          this.containerItem.quantityPerContainer = this.editingOrderItemContainerInfestation.infestationRateQuantity ?? 0;
          this.containerItem.numberOfContainers = this.editingOrderItemContainerInfestation.numberOfContainers ?? 0;
          this.updateVariables();
          afterRender(() => this.emitContainerItem());
        }
      }
    }

    if (changes['numberOfContainers']) {
      this.containerItem.numberOfContainers = this.numberOfContainers;
    }
  }

  getInsectsContainers(catalogInsectGrowthStageId: string): any[] {
    if (isEmpty(this.catalogInformation)) {
      return [];
    }

    const insectsContainersConcatenated: any = [];
    this.catalogInformation.forEach((item: any) => {
      item.Containers.forEach((container: any) => {
        if (!insectsContainersConcatenated.some((insect: any) => insect.value === container.CatalogContainerId)) {
          insectsContainersConcatenated.push({
            label: container.ContainerName,
            value: container.CatalogContainerInsectGrowthStageId,
            insectId: item.InsectId,
            leadTime: container.LeadTime,
            containerTypeName: container.ContainerTypeName,
            infestationRate: container.InfestationRate,
            catalogInsectGrowthStageId: container.CatalogInsectGrowthStageId,
            isAvailable: container.IsAvailable
          });
        }
      });
    });
    const result = insectsContainersConcatenated.filter(
      (container: any) => container.catalogInsectGrowthStageId === catalogInsectGrowthStageId && container.isAvailable === true
    );
    return result.sort((a: any, b: any) => a.label.localeCompare(b.label));
  }

  onChangeContainer(): void {
    this.containerItem.standard = true;
    this.containerItem.quantityPerContainer = null;
    this.containerItem.numberOfContainers = null;
    this.updateVariables();
    afterRender(() => this.emitContainerItem());
  }

  private updateVariables() {
    this.selectedContainer = this.insectsContainersConcatenated.find((container: any) =>
      isStringEqual(container.value, this.containerItem.catalogContainerInsectGrowthStageId as string)
    );

    const isEditingSelection =
      !isStringEmpty(this.editingOrderItemContainerInfestation?.catalogContainerInsectGrowthStageId) &&
      isStringEqual(
        this.editingOrderItemContainerInfestation.catalogContainerInsectGrowthStageId,
        this.containerItem.catalogContainerInsectGrowthStageId as string
      );

    if (this.selectedContainer) {
      const rate = this.selectedContainer.infestationRate;
      const effectiveRate = rate === 0 || rate === null || rate === undefined ? null : rate;
      this.quantityPerContainer =
        isEditingSelection && this.editingOrderItemContainerInfestation.infestationRateQuantity !== null
          ? this.editingOrderItemContainerInfestation.infestationRateQuantity
          : effectiveRate;
      this.containerItem.quantityPerContainer =
        isEditingSelection && this.editingOrderItemContainerInfestation.infestationRateQuantity !== null
          ? this.editingOrderItemContainerInfestation.infestationRateQuantity
          : effectiveRate;
      if (isEditingSelection && this.editingOrderItemContainerInfestation.standardInfestationRate !== null) {
        const newStandardValue =
          this.editingOrderItemContainerInfestation.standardInfestationRate === 1 ||
          this.editingOrderItemContainerInfestation.standardInfestationRate === true;
        this.containerItem.standard = newStandardValue;
      }
    } else {
      this.quantityPerContainer = null;
      this.containerItem.quantityPerContainer = null;
    }
  }

  onStandardToggle(event: any): void {
    if (event.checked) {
      const rate = this.selectedContainer?.infestationRate;
      this.containerItem.quantityPerContainer = rate === 0 || rate === null || rate === undefined ? null : rate;
    } else {
      this.containerItem.quantityPerContainer = this.quantityPerContainer ?? null;
    }
    afterRender(() => this.emitContainerItem());
  }

  onQuantityPerContainerChange(value: number | null): void {
    this.containerItem.quantityPerContainer = value;
    this.clearQuantityDebounceTimer();
    this.quantityDebounceTimer = setTimeout(() => {
      this.emitContainerItem();
      this.quantityDebounceTimer = null;
    }, 300);
  }

  ngOnDestroy(): void {
    this.clearQuantityDebounceTimer();
  }

  emitContainerItem(): void {
    this.containerItemChangeEvent.emit(this.containerItem);
  }

  private clearQuantityDebounceTimer(): void {
    if (this.quantityDebounceTimer) {
      clearTimeout(this.quantityDebounceTimer);
      this.quantityDebounceTimer = null;
    }
  }
}
